import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useFormikContext } from "formik";
import Select from "react-select";
import { Box, InputLabel, TextField } from "@mui/material";
import TextInputComponent from "../text-input-component/text-input-component";
import {
  addressData,
  extractAllOptions,
  filterOptionsByValue,
} from "../../../utils/extractCityStateZipCode";
import {
  cityOptions,
  stateOptions,
  codeOptions,
} from "../../../utils/extractCityStateZipCode";
import { debounce } from "lodash";
import "./address-component.scss";

type Props = {
  namePrefix?: string;
  postCode?: string;
  isOutlineInputs?: boolean;
  disabled?: boolean;
  displayStreetLines?: boolean;
  displayCountry?: boolean;
  displayBusinessAddress?: boolean;

  isProvider?: boolean;
  requiredFields?: {
    zipcode?: boolean;
    city?: boolean;
    state?: boolean;
    address?: boolean;
    country?: boolean;
  };
  customLabels?: {
    zipcode?: string;
    city?: string;
    state?: string;
    address?: string;
    country?: string;
  };
};

const countryOptions = [{ label: "United States", value: "United States" }];
const customSelectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
  }),
  valueContainer: (base: any) => ({
    ...base,
    height: '40px',
    padding: '0 8px',
  }),
  input: (base: any) => ({
    ...base,
    margin: '0px',
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: '40px',
  }),
};


const AddressFormFields: React.FC<Props> = ({
  namePrefix,
  postCode = "postcode",
  isOutlineInputs = false,
  disabled = false,
  displayStreetLines = true,
  displayCountry = true,
  displayBusinessAddress = false,
  requiredFields,
  customLabels,
  isProvider = false
}) => {
  const { values, setFieldValue, setFieldTouched, handleBlur, handleChange, errors, touched, submitCount } =
    useFormikContext<any>();
  const getField = (field: string) =>
    namePrefix ? `${namePrefix}.${field}` : `${field}`;
  const currentValues = namePrefix ? _.get(values, namePrefix) : values || {};
  const [zipCodePage, setZipCodePage] = useState(1);
  const PAGE_SIZE = 100;
  const [filteredOptions, setFilteredOptions] = useState<any>({
    cityOptions: cityOptions || [],
    stateOptions: stateOptions || [],
    postCodeOptions: codeOptions || [],
    filtered: [],
  });

  const getLabel = (
    fieldName: string,
    required?: boolean,
    customLabel?: string
  ) => {
    const labelText = customLabel || fieldName;

    if (!required) return labelText;

    return (
      <span>
        {labelText} <span style={{ color: "red" }}>*</span>
      </span>
    );
  };

  const [inputValue, setInputValue] = useState<any>({});

  const handleInputValue = useMemo(
    () =>
      debounce((value: string, field: string) => {
        setInputValue({ value, field });
      }, 500),
    []
  );

  const handleCityChange = (option: any) => {
    if (!option) {
      // User cleared the input
      setFieldValue(getField("city"), "");
      return;
    }
    setFieldValue(getField("city"), option?.value);

    const result = filterOptionsByValue(
      filteredOptions?.filtered,
      "City",
      option?.value
    );
    handleToCheckValueExitsInOptions(result, "city");
    setFilteredOptions({ ...result });
  };

  const findValueorNot = (options: any, value: string) => {
    return options?.find((opt: any) => opt.value === value) || null;
  };
  const getSelectedOption = (options: any[] = [], value?: string) => {
    if (!value) return null;
 
    const matchedOption = options.find((opt: any) => opt.value === value);
    if (matchedOption) return matchedOption;
 
    // fallback only for display purpose
    return { label: value, value };
  };
  const handleToCheckValueExitsInOptions = (options: any, key: string) => {
    if (
      currentValues?.state &&
      key !== "state" &&
      !findValueorNot(options?.stateOptions, currentValues?.state)
    ) {
      setFieldValue(getField("state"), "");
    }
    if (
      currentValues?.city &&
      key !== "city" &&
      !findValueorNot(options?.cityOptions, currentValues?.city)
    ) {
      setFieldValue(getField("city"), "");
    }
    if (
      currentValues?.[postCode] &&
      key !== "code" &&
      !findValueorNot(options?.postCodeOptions, currentValues?.[postCode])
    ) {
      setFieldValue(getField(postCode), "");
    }
  };

  const handlePincodeChange = (option: any) => {
    if (!option) {
      // User cleared the input
      setFieldValue(getField(postCode), "");
      setFilteredOptions((prev: any) => ({
        ...prev,
        postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE),
      }));
      setZipCodePage(1);
      return;
    }

    setFieldValue(getField(postCode), option?.value);
    const result = filterOptionsByValue(
      filteredOptions?.filtered,
      "Code",
      option?.value
    );
    handleToCheckValueExitsInOptions(result, "code");
    setFilteredOptions({ ...result });
  };

  const displayedZipCodes = useMemo(() => {
    setFilteredOptions((prev: any) => ({
      ...prev,
      postCodeOptions: codeOptions?.slice(0, zipCodePage * PAGE_SIZE),
    }));
    return codeOptions?.slice(0, zipCodePage * PAGE_SIZE);
  }, [zipCodePage]);

  useEffect(() => {
    if (inputValue?.value?.length >= 2) {
      const lower = inputValue?.value?.toLowerCase();
      const key = inputValue?.field;
      const data = key === "City" ? filteredOptions?.filtered : addressData;
      if (data?.length) {
        const filtered = data?.filter((c: any) =>
          c?.[key]?.toLowerCase().includes(lower)
        );
        const optionsData = extractAllOptions(filtered);
        if (key === "City") {
          setFilteredOptions((prev: any) => ({
            ...prev,
            cityOptions: optionsData?.cityOptions?.slice(0, 250),
            filtered,
          }));
        } else {
          const options: any = {};
          if (key === "State") {
            options.stateOptions = optionsData?.stateOptions?.slice(0, 100);
          }
          if (key === "Code") {
            options.postCodeOptions = optionsData?.postCodeOptions?.slice(
              0,
              250
            );
          }
          setFilteredOptions((prev: any) => ({
            ...prev,
            ...options,
            filtered,
          }));
        }
      }
    } else {
      setFilteredOptions({
        cityOptions: cityOptions || [],
        stateOptions: stateOptions || [],
        postCodeOptions: displayedZipCodes || [],
        filtered: [],
      });
    }
  }, [inputValue]);

  useEffect(() => {
    let result;

    if (currentValues?.[postCode]) {
      result = filterOptionsByValue(
        addressData,
        "Code",
        currentValues[postCode]
      );
    } else if (currentValues?.city) {
      result = filterOptionsByValue(
        addressData,
        "City",
        currentValues.city,
        "State",
        currentValues.state
      );
    } else if (currentValues?.state) {
      result = filterOptionsByValue(addressData, "State", currentValues.state);
    } else {
      // If no values are selected, reset options to default state
      setFilteredOptions({
        cityOptions: cityOptions || [],
        stateOptions: stateOptions || [],
        postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE) || [],
        filtered: [],
      });
      setZipCodePage(1);
      return;
    }
    if (result) {
      const noFilteredData = !result?.filtered?.length && !inputValue?.value;

      setFilteredOptions(
        noFilteredData
          ? {
            cityOptions: cityOptions || [],
            stateOptions: stateOptions || [],
            postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE) || [],
            filtered: [],
          }
          : { ...result }
      );
    }
  }, [currentValues?.[postCode], currentValues?.city, currentValues?.state]);

  return (
    <>
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr 1fr"
        gap={2}
        className="address-form-fields"
      >
        {displayStreetLines && (
          <TextInputComponent
            fieldName={getField("streetLine1")}
            label="Street Line 1"
            placeholder="Enter street line 1"
            value={currentValues?.streetLine1 || ""}
            onChange={handleChange}
            type="text"
            maxlength="50"
            isStreet
            disabled={disabled}
            autoComplete="new-password"
          />
        )}

        {displayStreetLines && (
          <TextInputComponent
            fieldName={getField("streetLine2")}
            label="Street Line 2"
            placeholder="Enter street line 2"
            value={currentValues?.streetLine2 || ""}
            onChange={handleChange}
            type="text"
            maxlength="50"
            isStreet
            disabled={disabled}
            autoComplete="new-password"
          />
        )}

        {/* Postcode */}
        <div className="form-group">
          {isProvider ? <InputLabel htmlFor="Postcode" sx={{ color: "#000", fontWeight: 500, mb: 1 }} >
            {getLabel("Zipcode", requiredFields?.zipcode, customLabels?.zipcode)}
          </InputLabel> : <InputLabel htmlFor="Postcode">Zip Code</InputLabel>}
          <Select
            inputId="Postcode"
            classNamePrefix="react-select"
            options={filteredOptions.postCodeOptions}
            placeholder="Select zip code"
            components={{
              IndicatorSeparator: () => null,
            }}
            value={getSelectedOption(filteredOptions.postCodeOptions, currentValues?.[postCode])}
            // onInputChange={(val) => handleInputValue(val, "Code")}
            onInputChange={(val, actionMeta) => {
              if (actionMeta.action === "input-change") {
                handleInputValue(val, "Code");
              }
            }}
            onMenuScrollToBottom={() => {
              //if there is no state and city selected, load more zip codes
              //if there is state or city selected then based on that value we will get zip codes
              if (
                !currentValues?.state &&
                !currentValues?.city &&
                inputValue.field !== "Code" &&
                zipCodePage * PAGE_SIZE < codeOptions?.length
              ) {
                setZipCodePage((prev) => prev + 1);
              }
            }}
            onChange={handlePincodeChange}
            onBlur={() => setFieldTouched(getField(postCode), true)}
            isClearable={true}
            isSearchable={true}
            isDisabled={disabled}
            styles={customSelectStyles}
          />
          {( (_.get(touched, getField(postCode)) || submitCount > 0) &&
            _.get(errors, getField(postCode)) ) ? (
            <p className="address-form-fields__error">
              {String(_.get(errors, getField(postCode)))}
            </p>
          ) : null}
        </div>

        {/* City */}
        <div className="form-group">
          {isProvider ? <InputLabel sx={{ color: "#000", fontWeight: 500, mb: 1 }}>
            {getLabel("City", requiredFields?.city, customLabels?.city)}
          </InputLabel> : <InputLabel htmlFor="City">City</InputLabel>}
          <Select
            inputId="City"
            classNamePrefix="react-select"
            options={filteredOptions.cityOptions?.sort((a: any, b: any) =>
              a?.value.localeCompare(b?.value)
            )}
            placeholder="Select city"
            isDisabled={
              (!currentValues?.state && !currentValues?.[postCode]) || disabled
            }
            value={getSelectedOption(filteredOptions.cityOptions, currentValues?.city)}
            components={{
              IndicatorSeparator: () => null,
            }}
            onChange={handleCityChange}
            onBlur={() => setFieldTouched(getField("city"), true)}
            isClearable={true}
            isSearchable={true}
            styles={customSelectStyles}
          />
          {( (_.get(touched, getField("city")) || submitCount > 0) &&
            _.get(errors, getField("city")) ) ? (
            <p className="address-form-fields__error">
              {String(_.get(errors, getField("city")))}
            </p>
          ) : null}
        </div>
        {/* State */}
        <div className="form-group">
          {isProvider ? <InputLabel sx={{ color: "#000", fontWeight: 500, mb: 1 }}>
            {getLabel("State", requiredFields?.state, customLabels?.state)}
          </InputLabel> : <InputLabel htmlFor="State">State</InputLabel>}
          <Select
            inputId="State"
            options={filteredOptions.stateOptions?.sort((a: any, b: any) =>
              a?.value.localeCompare(b?.value)
            )}
            placeholder="Select state"
            classNamePrefix="react-select"
            required={false}
            value={
              filteredOptions?.stateOptions?.find(
                (opt: any) => opt.value === currentValues?.state
              ) || null
            }
            components={{
              IndicatorSeparator: () => null,
            }}
            // onInputChange={(val) => handleInputValue(val, "State")}
            onInputChange={(val, actionMeta) => {
              if (actionMeta.action === "input-change") {
                handleInputValue(val, "State");
              }
            }}
            onChange={(option: any) => {
              if (!option) {
                // User cleared the input
                setFilteredOptions((prev: any) => ({
                  cityOptions: [],
                  postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE),
                  filtered: [],
                  stateOptions: stateOptions, // reset full list
                }));
                setFieldValue(getField("state"), "");
                setFieldValue(getField("city"), "");
                setFieldValue(getField(postCode), "");
                setZipCodePage(1);
                return;
              }
              const result = filterOptionsByValue(
                filteredOptions?.filtered,
                "State",
                option?.value
              );
              handleToCheckValueExitsInOptions(result, "state");
              setFilteredOptions({ ...result });
              setFieldValue(getField("state"), option?.value);
            }}
            isClearable={true}
            isSearchable={true}
            isDisabled={disabled}
            styles={customSelectStyles}
            onBlur={() => setFieldTouched(getField("state"), true)}
          />
          {( (_.get(touched, getField("state")) || submitCount > 0) &&
            _.get(errors, getField("state")) ) ? (
            <p className="address-form-fields__error">
              {String(_.get(errors, getField("state")))}
            </p>
          ) : null}
        </div>

        {/* Country (static options, if needed) */}
        {displayCountry && (
          <div className="form-group">
            {isProvider ? <InputLabel sx={{ color: "#000", fontWeight: 500, mb: 1 }}>
            {getLabel("Country", requiredFields?.country, customLabels?.country)}
          </InputLabel> : <InputLabel htmlFor="Country">Country</InputLabel>}
            <Select
              inputId="Country"
              classNamePrefix="react-select"
              options={countryOptions}
              placeholder="Select country"
              components={{
                IndicatorSeparator: () => null,
              }}
              value={
                countryOptions.find(
                  (opt: any) => opt.value === currentValues?.country
                ) || null
              }
              onChange={(option: any) =>
                setFieldValue(getField("country"), option?.value)
              }
              onBlur={() => setFieldTouched(getField("country"), true)}
              isClearable={false}
              isDisabled={disabled}
              styles={customSelectStyles}
            />
            {( (_.get(touched, getField("country")) || submitCount > 0) &&
              _.get(errors, getField("country")) ) ? (
              <p className="address-form-fields__error">
                {String(_.get(errors, getField("country")))}
              </p>
            ) : null}
          </div>
        )}
      </Box>
      {displayBusinessAddress && (
        <Box mt={2}>
          <InputLabel sx={{ color: "#000", fontWeight: 500, mb: 1 }}>
            {getLabel("Business Address", requiredFields?.address, customLabels?.address)}
          </InputLabel>
          <TextField
            id={getField("address")}
            placeholder="Enter Business Address"
            fullWidth
            value={currentValues?.address || ""}
            onChange={(e) => setFieldValue(getField("address"), e.target.value)}
            error={!!_.get(errors, getField("address"))} // show error
            helperText={
              _.get(errors, getField("address"))
                ? String(_.get(errors, getField("address")))
                : ""
            }
            size="small"
            InputProps={{
              sx: {
                height: "40px",
                '& .MuiInputBase-input': {
                  padding: "8px 12px",
                },
              },
            }}

          />
        </Box>
      )}
    </>
  );
};

export default AddressFormFields;
